import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, UserCheck, ArrowRight, ShieldCheck } from 'lucide-react';

export default function Login() {
  const [formData, setFormData] = useState({ identifier: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Direct relative path works for both Local Dev (with proxy/relative) and Hostinger Production
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (data.success) {
        // Store both JWT Token for security headers and User profile data
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Redirect to Courses dashboard
        navigate('/courses');
      } else {
        setError(data.message || 'Invalid credentials. Please try again.');
      }
    } catch (err) {
      console.error('Login Error:', err);
      setError('Server connection error. Please check if the server is running.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[85vh] flex items-center justify-center bg-[#FAF8F5] px-6 py-12 relative overflow-hidden">
      
      {/* Background Animated Glows */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-[#16325B]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-[#2563EB]/10 rounded-full blur-3xl pointer-events-none" />

      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white/90 backdrop-blur-xl p-8 sm:p-12 rounded-3xl border border-gray-200/80 shadow-xl max-w-md w-full space-y-6 relative z-10"
      >
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-1.5 bg-[#EEF4FA] text-[#16325B] text-[11px] font-bold px-3 py-1 rounded-full border border-[#C7D7E8]">
            <ShieldCheck className="w-3.5 h-3.5" /> Secure Member Portal
          </div>
          <h2 className="font-serif-title text-3xl font-extrabold text-[#0B2540]">Welcome Back</h2>
          <p className="text-xs text-gray-500">Access your unlocked courses and wealth advisory tools.</p>
        </div>

        {error && (
          <motion.p 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-xs font-bold text-red-600 bg-red-50 p-3 rounded-xl border border-red-200 text-center"
          >
            {error}
          </motion.p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-1.5">
              Email Address or Phone Number
            </label>
            <div className="relative">
              <UserCheck className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
              <input 
                type="text" 
                required
                placeholder="Email or Phone (e.g. +91 9876543210)"
                className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl py-3 pl-10 pr-4 text-xs sm:text-sm focus:outline-none focus:border-[#16325B] transition" 
                value={formData.identifier}
                onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider">Password</label>
              <Link to="/forgot-password" className="text-[11px] font-bold text-[#16325B] hover:underline">
                Forgot Password?
              </Link>
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
              <input 
                type="password" 
                required
                placeholder="••••••••"
                className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl py-3 pl-10 pr-4 text-xs sm:text-sm focus:outline-none focus:border-[#16325B] transition" 
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>
          </div>

          <motion.button 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit" 
            disabled={loading}
            className="w-full bg-[#16325B] hover:bg-[#0F213D] text-white py-3.5 rounded-xl font-bold text-xs sm:text-sm transition duration-300 shadow-md flex items-center justify-center gap-2 disabled:opacity-50 mt-2"
          >
            {loading ? 'Authenticating...' : 'Sign In to Account'}
            <ArrowRight className="w-4 h-4" />
          </motion.button>
        </form>

        <p className="text-xs text-center text-gray-500 font-medium">
          Don't have an account? <Link to="/signup" className="text-[#16325B] font-bold hover:underline">Sign Up with OTP</Link>
        </p>
      </motion.div>
    </div>
  );
}